import { useState, useCallback, useEffect, useRef, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { Button } from "@knowtex-ai/components";
import { Microphone, Page, Square } from "iconoir-react";
import { CHAPI_FIELDS } from "./utils/chapidata";
import { buildSelectedSmartAssistChartHistory } from "./utils/smartAssistChartHistory";
import { shouldReplaceAdditionalContextDraft } from "./utils/additionalContextHydration";
import { FieldIcon, RefreshIcon, CheckIcon, EyeIcon } from "./utils/icons";
import NotePreview from "./NotePreview";
import PreviousNoteModal from "./PreviousNoteModal";
import styles from "./PrechartingModal.module.css";
import { useTutorial } from "../../Tutorial/useTutorial";
import {
  buildSmartChartSection,
  waitForSmartChartAnchors,
  SMART_CHART_PAGE_ID,
} from "../../Tutorial/utils/smartChartSlides";
import { useFeatureGate } from "../../../hooks/useFeatureGate";
import { isStandaloneUser as getIsStandaloneUser } from "../../../utils/standaloneUser";
import { MacrosContext } from "../../../utils/MacrosContext";
import { mergeTelehealthAvailableMacros } from "../../../utils/telehealthMacros";
import { buildDotPhraseMap } from "../../../utils/dotPhraseMap";
import { useSharedMacrosQuery } from "../../../hooks/useSharedMacros";
import patientChatApi from "../../../lib/patientChat";
import {
  usePatientChatCapabilities,
  usePatientChatDraftSaver,
} from "../../../hooks/usePatientChat";
import AskKnowtexSidecar, {
  SMART_ASSIST_DEFAULT_WIDTH_PERCENT,
  SMART_ASSIST_SPLIT_MIN_WIDTH,
  useSmartAssistLayout,
} from "../../noteList/AskKnowtexSidecar";
import SmartAssistPanel from "../../smartAssist/hosts/SmartAssistPanel";
import { useSmartAssistPanelState } from "../../../hooks/useProviderSettings";
import { joinDictatedDateAcrossChunks } from "../../noteList/dictationDates";
import {
  capitalizeFirstDictationWord,
  isDictationLineBreakOnly,
  isDictationPunctuationOnly,
  normalizePhoneNumberContinuation,
  normalizeSpokenDictation,
  shouldCapitalizeNextDictationWord,
} from "../../noteList/spokenDictationRules";
import { getSessionToken } from "../../../lib/sessionToken";
import { logInfo } from "../../../lib/sentryLogging";

const INITIAL_SELECTIONS = {
  vitals: false,
  labs: false,
  medications: false,
  allergies: false,
  past_medical_history: false,
};

const WORKSPACE_ESCAPE_LOCAL_TARGET_SELECTOR = [
  "textarea",
  "input:not([type])",
  'input[type="text"]',
  'input[type="search"]',
  'input[type="email"]',
  'input[type="url"]',
  'input[type="tel"]',
  'input[type="password"]',
  'input[type="number"]',
  '[contenteditable]:not([contenteditable="false"])',
  '[role="alertdialog"]',
  '[role="dialog"]',
].join(",");

const parseChartHistory = (rawChartHistory) => {
  if (!rawChartHistory) return {};
  if (typeof rawChartHistory === "string") {
    try {
      return JSON.parse(rawChartHistory);
    } catch (error) {
      console.warn("Failed to parse chart history in Smart Chart:", error);
      return {};
    }
  }
  if (typeof rawChartHistory === "object") return rawChartHistory;
  return {};
};

const extractChartHistoryPayload = (apiResponse) => {
  if (!apiResponse) return {};

  const candidates = [
    apiResponse?.chartHistory,
    apiResponse?.chart_history,
    apiResponse?.data?.chartHistory,
    apiResponse?.data?.chart_history,
    apiResponse?.patient?.chartHistory,
    apiResponse?.patient?.chart_history,
    apiResponse,
  ];

  for (const candidate of candidates) {
    const parsed = parseChartHistory(candidate);
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length > 0) {
      return parsed;
    }
  }

  return {};
};

const normalizeStringValue = (value) => {
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value.trim() : String(value).trim();
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  if (typeof value === "number" || typeof value === "boolean") return true;
  return String(value).trim().length > 0;
};

const formatFieldPreviewValue = (value) => {
  if (!hasMeaningfulValue(value)) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

const resolveFieldPreviewValue = (field, chartHistorySource = {}, chartHistoryValues = {}) => {
  const aliases = Array.isArray(field?.aliases) ? field.aliases : [];
  for (const alias of aliases) {
    const rawValue = chartHistorySource?.[alias];
    if (hasMeaningfulValue(rawValue)) {
      return formatFieldPreviewValue(rawValue);
    }
  }

  return formatFieldPreviewValue(chartHistoryValues?.[field?.key]);
};

const normalizeAdditionalContextValue = (value) => {
  if (value === null || value === undefined) return "";
  return typeof value === "string" ? value : String(value);
};

const getComparableAdditionalContextValue = (value) =>
  normalizeAdditionalContextValue(value).trim();
const hasFieldTextBlockInAdditionalContext = (currentValue, fieldText) => {
  const normalizedCurrent = normalizeAdditionalContextValue(currentValue);
  const normalizedFieldText = normalizeAdditionalContextValue(fieldText).trim();

  if (!normalizedFieldText) {
    return false;
  }

  return normalizedCurrent.includes(normalizedFieldText);
};

const SMART_CHART_DEEPGRAM_WS_URL = "wss://api.deepgram.com/v1/listen";
const SMART_CHART_DEEPGRAM_MIME_TYPE_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm"];

const getSupportedSmartChartRecordingMimeType = () => {
  if (typeof MediaRecorder === "undefined") {
    return "";
  }

  if (typeof MediaRecorder.isTypeSupported !== "function") {
    return SMART_CHART_DEEPGRAM_MIME_TYPE_CANDIDATES[0];
  }

  return (
    SMART_CHART_DEEPGRAM_MIME_TYPE_CANDIDATES.find((mimeType) =>
      MediaRecorder.isTypeSupported(mimeType)
    ) || ""
  );
};

const getDictationMicrophoneErrorMessage = (error) => {
  const errorName = typeof error?.name === "string" ? error.name : "";

  if (errorName === "NotAllowedError" || errorName === "SecurityError") {
    return "Microphone access was blocked. Enable microphone permissions and try again.";
  }

  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return "No microphone was found. Connect a microphone and try again.";
  }

  if (errorName === "NotReadableError" || errorName === "TrackStartError") {
    return "The microphone is unavailable. Close other apps using it and try again.";
  }

  return "Unable to access the microphone. Check your browser microphone settings and try again.";
};
const appendFieldTextToAdditionalContext = (currentValue, fieldText) => {
  const normalizedCurrent = normalizeAdditionalContextValue(currentValue).trim();
  const normalizedFieldText = normalizeAdditionalContextValue(fieldText).trim();

  if (!normalizedFieldText) {
    return normalizedCurrent;
  }

  if (hasFieldTextBlockInAdditionalContext(normalizedCurrent, normalizedFieldText)) {
    return normalizedCurrent;
  }

  return normalizedCurrent ? `${normalizedCurrent}\n\n${normalizedFieldText}` : normalizedFieldText;
};
const removeFieldTextFromAdditionalContext = (currentValue, fieldText) => {
  const normalizedCurrent = normalizeAdditionalContextValue(currentValue);
  const normalizedFieldText = normalizeAdditionalContextValue(fieldText).trim();

  if (!normalizedFieldText) {
    return normalizedCurrent;
  }

  return normalizedCurrent
    .split(normalizedFieldText)
    .join("")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};
const toggleFieldTextInAdditionalContext = (currentValue, fieldText) =>
  hasFieldTextBlockInAdditionalContext(currentValue, fieldText)
    ? removeFieldTextFromAdditionalContext(currentValue, fieldText)
    : appendFieldTextToAdditionalContext(currentValue, fieldText);

const getDeletedFieldStorageKey = (field = {}) =>
  (Array.isArray(field.aliases) && field.aliases[0]) || field.key;

const getWritableAliases = (field = {}) => {
  if (field.key === "past_medical_history") {
    return ["past_medical_history"];
  }
  return Array.isArray(field.aliases) ? field.aliases : [];
};

const getFirstResolvedAliasValue = (source = {}, aliases = []) => {
  for (const alias of aliases) {
    const normalizedValue = normalizeStringValue(source?.[alias]);
    if (normalizedValue) {
      return normalizedValue;
    }
  }
  return "";
};

const normalizeDeletedFields = (deletedFieldsValue) => {
  if (!deletedFieldsValue || typeof deletedFieldsValue !== "object") {
    return {};
  }

  return Object.entries(deletedFieldsValue).reduce((acc, [key, value]) => {
    const normalizedKey = key === "labs" ? "lab_results" : key;
    const normalizedValue = normalizeStringValue(value);
    if (normalizedValue) {
      acc[normalizedKey] = normalizedValue;
    }
    return acc;
  }, {});
};

const isPrechartChartHistory = (chartHistory = {}) => {
  const flag = chartHistory?.is_prechart;
  return flag === true || String(flag || "").toLowerCase() === "true";
};
const isCustomTemplateChartHistory = (chartHistory = {}) => {
  const flag = chartHistory?.is_custom_template;
  return flag === true || String(flag || "").toLowerCase() === "true";
};

const SMART_CHART_TABS = {
  SMART_CHART: "smart_chart",
  CUSTOM_PROMPT: "custom_prompt",
  SMART_ASSIST: "smart_assist",
};

const CUSTOM_TEMPLATE_FIELD = {
  key: "custom_template",
  label: "Additional EHR Context",
  bg: "#ecfeff",
  color: "#0e7490",
  d: "M5 3h14a2 2 0 0 1 2 2v14|M7 7h10|M7 11h10|M7 15h6",
};

const CUSTOM_TEMPLATE_CONSTRUCTOR_TOOLTIP =
  "Paste in important context such as text from previous notes, imaging findings, or labs. Additional EHR Context added will be synthesized and carried forward into the note unless explicitly updated during the patient visit.";
const CUSTOM_PROMPT_STARTER = `Follow the instructions below when formatting the note and creating the final output.

GENERAL RULES:
[Any rules that apply to the whole note, such as "use Veteran in the note instead of patient's name" or "write each section in bullet points"]

Structure the note in the following order only: [list out each section title in order you want them to appear in your note, such as "SUBJECTIVE, OBJECTIVE, ASSESSMENT, PLAN"].

For X, [any specific instructions for section X],
For Y, [specific instructions for section Y],
...`;
const SMART_CHART_CONSENT_DISCLAIMER =
  "Patient or surrogate consented to the use of the AI Scribe. Provider is responsible for reviewing and confirming the accuracy of this note.";
const ensureSmartChartConsentDisclaimer = (rawNote = "") => {
  const normalizedNote = normalizeAdditionalContextValue(rawNote).trim();
  if (!normalizedNote) {
    return SMART_CHART_CONSENT_DISCLAIMER;
  }

  const escapedDisclaimer = SMART_CHART_CONSENT_DISCLAIMER.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const disclaimerRegex = new RegExp(escapedDisclaimer, "g");
  const noteWithoutDisclaimer = normalizedNote.replace(disclaimerRegex, "").trim();
  return `${noteWithoutDisclaimer}\n\n${SMART_CHART_CONSENT_DISCLAIMER}`;
};
const stripSmartChartConsentDisclaimer = (rawNote = "") => {
  const normalizedNote = normalizeAdditionalContextValue(rawNote).trim();
  if (!normalizedNote) {
    return "";
  }

  const escapedDisclaimer = SMART_CHART_CONSENT_DISCLAIMER.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const disclaimerRegex = new RegExp(escapedDisclaimer, "g");
  return normalizedNote.replace(disclaimerRegex, "").trim();
};

export default function PrechartingModal({
  patientName = "Jane Doe",
  patientID,
  appointmentId,
  specialty = "",
  diarizedTranscript,
  noteCitations,
  onClose,
  onSubmitSuccess,
  onCustomPromptSaved,
  setShowPrechartingModal,
  crossSiteCareStatus = null,
}) {
  const isStandaloneUser = getIsStandaloneUser();
  const crossSiteRelaunchMessage = crossSiteCareStatus
    ? `Relaunch Knowtex from ${crossSiteCareStatus.facilityName} (${crossSiteCareStatus.displayName}, Station ${crossSiteCareStatus.sta3n}) before using Refetch Data or updating EHR-sourced information. Station-dependent requests will not work from the current Point of Care.`
    : "";
  const { userMacros, fetchUserMacros } = useContext(MacrosContext);
  const sharedMacros = useSharedMacrosQuery();
  const vvcDotphraseGate = useFeatureGate("vvc-dotphrase");
  const hasVvcDotphraseAccess = vvcDotphraseGate.hasFeature("on");
  const getLastNoteGate = useFeatureGate("get-last-note");
  const hasGetLastNoteAccess = getLastNoteGate.hasFeature("on");
  const dialogRef = useRef(null);
  const workspaceRef = useRef(null);
  const smartAssistStateCacheRef = useRef(null);
  const smartAssistDraftSaver = usePatientChatDraftSaver();
  const closeButtonRef = useRef(null);
  const customTemplateTextareaRef = useRef(null);
  const additionalContextSaveButtonRef = useRef(null);
  const customPromptTextareaRef = useRef(null);
  const customPromptSaveButtonRef = useRef(null);
  const hasFetchedCustomPromptMacrosRef = useRef(false);
  const hasUserSelectedTabRef = useRef(false);
  const currentPatientIdRef = useRef(patientID);
  currentPatientIdRef.current = patientID;
  const [selections, setSelections] = useState(INITIAL_SELECTIONS);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [chartHistoryValues, setChartHistoryValues] = useState({});
  const [chartHistorySource, setChartHistorySource] = useState({});
  const [availableFields, setAvailableFields] = useState([]);
  const [previewNote, setPreviewNote] = useState("");
  const [previewError, setPreviewError] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [hasFetchedChartHistory, setHasFetchedChartHistory] = useState(false);
  const [activeTab, setActiveTab] = useState(SMART_CHART_TABS.SMART_CHART);
  const [additionalContextDraft, setAdditionalContextDraft] = useState("");
  const [previewAdditionalContextDraft, setPreviewAdditionalContextDraft] = useState("");
  const [savedAdditionalContext, setSavedAdditionalContext] = useState("");
  const [savedStartingNote, setSavedStartingNote] = useState("");
  const [isSavingAdditionalContext, setIsSavingAdditionalContext] = useState(false);
  const [additionalContextError, setAdditionalContextError] = useState(null);
  const [fetchedPatientIcn, setFetchedPatientIcn] = useState("");
  const [isPreviousNoteModalOpen, setIsPreviousNoteModalOpen] = useState(false);
  const [queuedSmartAssistPrompt, setQueuedSmartAssistPrompt] = useState("");
  const [isAdditionalContextExpanded, setIsAdditionalContextExpanded] = useState(false);
  const [isAdditionalContextDictating, setIsAdditionalContextDictating] = useState(false);
  const [isAdditionalContextDictationStarting, setIsAdditionalContextDictationStarting] =
    useState(false);
  const [additionalContextDictationStatus, setAdditionalContextDictationStatus] = useState("");
  const [customPromptDraft, setCustomPromptDraft] = useState("");
  const [savedCustomPrompt, setSavedCustomPrompt] = useState("");
  const [isSavingCustomPrompt, setIsSavingCustomPrompt] = useState(false);
  const [customPromptError, setCustomPromptError] = useState(null);
  const {
    isExpanded: isSmartAssistExpanded,
    setIsExpanded: setIsSmartAssistExpanded,
    setPreferredExpanded: setSmartAssistPreferredExpanded,
  } = useSmartAssistPanelState();
  const [isSmartAssistDictationActive, setIsSmartAssistDictationActive] = useState(false);
  const [smartAssistWidthPercent, setSmartAssistWidthPercent] = useState(
    SMART_ASSIST_DEFAULT_WIDTH_PERCENT
  );
  const [smartAssistContainerWidth, setSmartAssistContainerWidth] = useState(() =>
    typeof window === "undefined" ? SMART_ASSIST_SPLIT_MIN_WIDTH : window.innerWidth
  );
  const previewRequestIdRef = useRef(0);
  const additionalContextDictationSocketRef = useRef(null);
  const additionalContextDictationRecorderRef = useRef(null);
  const additionalContextDictationStreamRef = useRef(null);
  const additionalContextDictationInterimRangeRef = useRef(null);
  const additionalContextLastFinalDictationRef = useRef({
    text: "",
    insertedAt: 0,
  });
  const smartAssistCapabilitiesQuery = usePatientChatCapabilities({
    patientId: patientID,
    enabled: Boolean(patientID),
  });
  const isSmartAssistAvailable = smartAssistCapabilitiesQuery.data?.enabled;
  const { contentScale: smartAssistContentScale, isSplitView: isSmartAssistSplitView } =
    useSmartAssistLayout(smartAssistContainerWidth);
  const isSmartAssistOpen =
    isSmartAssistAvailable &&
    (isSmartAssistSplitView ? isSmartAssistExpanded : activeTab === SMART_CHART_TABS.SMART_ASSIST);
  const effectivePatientIcn = normalizeStringValue(appointmentId || fetchedPatientIcn);
  const hasAppointmentId = effectivePatientIcn.length > 0;
  const isAdditionalContextOnlyPatient = !hasAppointmentId;
  const hasExistingPrechartState = isPrechartChartHistory(chartHistorySource);

  useEffect(() => {
    document.body.classList.add("smart-chart-open");

    return () => {
      document.body.classList.remove("smart-chart-open");
    };
  }, []);

  useEffect(() => {
    if (!isSmartAssistOpen) {
      return undefined;
    }

    document.body.classList.add("smart-assist-open");

    return () => {
      document.body.classList.remove("smart-assist-open");
    };
  }, [isSmartAssistOpen]);

  useEffect(() => {
    if (!isSmartAssistAvailable || !isSmartAssistSplitView) {
      return undefined;
    }

    document.body.classList.add("smart-chart-2b-has-smart-assist");
    return () => {
      document.body.classList.remove("smart-chart-2b-has-smart-assist");
    };
  }, [isSmartAssistAvailable, isSmartAssistSplitView]);

  const isWorkspaceSmartAssistCoveringNavigation =
    isSmartAssistAvailable && isSmartAssistSplitView && isSmartAssistExpanded;
  useEffect(() => {
    if (!isWorkspaceSmartAssistCoveringNavigation) {
      return undefined;
    }
    // The expanded panel sits over the primary navigation, so keep Tab and
    // screen readers off links nobody can see.
    const navigation = document.getElementById("primary-navigation");
    if (!navigation || navigation.hasAttribute("inert")) {
      return undefined;
    }
    navigation.setAttribute("inert", "");
    return () => navigation.removeAttribute("inert");
  }, [isWorkspaceSmartAssistCoveringNavigation]);

  useEffect(() => {
    if (!isSmartAssistAvailable || !isSmartAssistSplitView || !isSmartAssistExpanded) {
      return undefined;
    }

    const propertyName = "--smart-chart-2b-smart-assist-width";
    const previousWidth = document.body.style.getPropertyValue(propertyName);
    document.body.style.setProperty(propertyName, `${smartAssistWidthPercent}vw`);

    return () => {
      if (previousWidth) {
        document.body.style.setProperty(propertyName, previousWidth);
      } else {
        document.body.style.removeProperty(propertyName);
      }
    };
  }, [isSmartAssistAvailable, isSmartAssistExpanded, isSmartAssistSplitView, smartAssistWidthPercent]);

  // First-open Smart Chart training: the first time a user ever opens the
  // Smart Chart window, auto-run the Smart Chart slides. The viewed flag is
  // persisted per user (pageTutorials.smart_chart), so this happens exactly
  // once; afterwards the slides remain available from the "i" button.
  const {
    isActive: isTutorialActive,
    isLoading: isTutorialLoading,
    tutorialData,
    setTutorialSteps,
    startTutorial,
    markPageTutorialViewed,
  } = useTutorial();
  const hasAttemptedSmartChartAutoStartRef = useRef(false);
  useEffect(() => {
    if (
      isTutorialLoading ||
      isTutorialActive ||
      !tutorialData ||
      typeof markPageTutorialViewed !== "function"
    ) {
      return undefined;
    }
    // Auto-open only when the user has NEVER seen the Smart Chart training,
    // i.e. there is no `smart_chart` entry in their walkthrough JSON yet. The
    // presence of the key (whatever its value) means it has already run once,
    // so skip. markPageTutorialViewed writes the key below, so it opens exactly
    // once; remove the `smart_chart` key from webapp_walkthrough to re-trigger.
    const hasSmartChartEntry =
      tutorialData?.pageTutorials != null &&
      Object.prototype.hasOwnProperty.call(tutorialData.pageTutorials, SMART_CHART_PAGE_ID);
    if (hasSmartChartEntry) {
      return undefined;
    }
    if (hasAttemptedSmartChartAutoStartRef.current) {
      return undefined;
    }

    let cancelled = false;
    const isAborted = () =>
      cancelled || hasAttemptedSmartChartAutoStartRef.current || hasUserSelectedTabRef.current;

    const openSmartChartTraining = async () => {
      // Let the window's open animation settle before the first slide appears.
      await new Promise((resolve) => setTimeout(resolve, 800));
      if (isAborted()) return;
      // Start on the Smart Chart tab so only the Smart Chart slides run.
      const smartChartTab = document.querySelector("#smart-chart-tab");
      if (smartChartTab && smartChartTab.getAttribute("aria-selected") !== "true") {
        smartChartTab.click();
      }
      // Build only once that panel and its anchors are mounted, so the tour
      // isn't missing the slides that anchor inside it.
      await waitForSmartChartAnchors({ isAborted });
      if (isAborted()) return;
      const smartChartSection = buildSmartChartSection();
      if (!smartChartSection) {
        return;
      }
      // Consume the one-shot guard only now that we're actually starting, so a
      // StrictMode / dependency-change re-run that cancels the pending open
      // before it fires can't permanently block the auto-launch.
      hasAttemptedSmartChartAutoStartRef.current = true;
      markPageTutorialViewed(SMART_CHART_PAGE_ID);
      setTutorialSteps([smartChartSection]);
      startTutorial({
        resume: false,
        startSectionId: smartChartSection.id,
        startStepId: smartChartSection.steps[0].id,
        expectedStepCount: smartChartSection.steps.length,
      });
    };
    openSmartChartTraining();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTutorialLoading, isTutorialActive, tutorialData]);

  const selectedCount = availableFields.filter((field) => selections[field.key]).length;
  const totalChapiFieldCount = CHAPI_FIELDS.length;

  // Smart Assist should only see the smart-chart fields the clinician has
  // selected here, not the full raw chart history payload. Fields the
  // clinician has not checked are omitted entirely, matching what the note
  // preview already does for `previewNote`.
  const smartAssistChartHistory = useMemo(
    () =>
      buildSelectedSmartAssistChartHistory(
        chartHistorySource,
        selections,
        availableFields.length > 0 ? availableFields : CHAPI_FIELDS
      ),
    [availableFields, chartHistorySource, selections]
  );

  const selectableFields = availableFields.filter((field) =>
    Boolean(chartHistoryValues[field.key])
  );
  const hasSelectableFields = selectableFields.length > 0;
  const selectableAdditionalContextFieldEntries = availableFields
    .map((field) => ({
      key: field.key,
      value: normalizeAdditionalContextValue(
        resolveFieldPreviewValue(field, chartHistorySource, chartHistoryValues)
      ).trim(),
    }))
    .filter((entry) => entry.value);
  const hasSelectableAdditionalContextFields = selectableAdditionalContextFieldEntries.length > 0;
  const areAllSelectableAdditionalContextFieldsIncluded =
    hasSelectableAdditionalContextFields &&
    selectableAdditionalContextFieldEntries.every((entry) =>
      hasFieldTextBlockInAdditionalContext(additionalContextDraft, entry.value)
    );
  const additionalContextInsertedCount = selectableAdditionalContextFieldEntries.filter((entry) =>
    hasFieldTextBlockInAdditionalContext(additionalContextDraft, entry.value)
  ).length;
  const areAllSelectableFieldsSelected =
    hasSelectableFields && selectableFields.every((field) => Boolean(selections[field.key]));
  const hasUnsavedAdditionalContext =
    getComparableAdditionalContextValue(additionalContextDraft) !==
    getComparableAdditionalContextValue(savedAdditionalContext);
  const hasSavedCustomTemplateInChartHistory = isCustomTemplateChartHistory(chartHistorySource);
  const additionalContextStatus = isSavingAdditionalContext
    ? "saving"
    : hasUnsavedAdditionalContext
      ? "unsaved"
      : hasSavedCustomTemplateInChartHistory
        ? "saved"
        : "empty";
  const additionalContextStatusLabel =
    additionalContextStatus === "saving"
      ? "Saving..."
      : additionalContextStatus === "unsaved"
        ? "Unsaved"
        : additionalContextStatus === "saved"
          ? "Saved"
          : "Empty";
  const previewBadgeStatusClass =
    additionalContextStatus === "saving"
      ? styles.previewBadgeStatusSaving
      : additionalContextStatus === "unsaved"
        ? styles.previewBadgeStatusUnsaved
        : additionalContextStatus === "saved"
          ? styles.previewBadgeStatusSaved
          : styles.previewBadgeStatusEmpty;
  const hasUnsavedCustomPrompt =
    normalizeAdditionalContextValue(customPromptDraft) !==
    normalizeAdditionalContextValue(savedCustomPrompt);
  const hasCustomPromptDraft = getComparableAdditionalContextValue(customPromptDraft).length > 0;
  const isSaveDisabled =
    isSubmitting ||
    isLoading ||
    (selectedCount === 0 &&
      getComparableAdditionalContextValue(
        isAdditionalContextExpanded ? additionalContextDraft : previewAdditionalContextDraft
      ).length === 0 &&
      !hasExistingPrechartState &&
      !hasCustomPromptDraft);
  const hasSavedCustomPrompt = getComparableAdditionalContextValue(savedCustomPrompt).length > 0;
  const customPromptStatus = isSavingCustomPrompt
    ? "saving"
    : hasUnsavedCustomPrompt
      ? "unsaved"
      : hasSavedCustomPrompt
        ? "saved"
        : "empty";
  const customPromptStatusLabel =
    customPromptStatus === "saving"
      ? "Saving..."
      : customPromptStatus === "unsaved"
        ? "Unsaved"
        : customPromptStatus === "saved"
          ? "Saved"
          : "Empty";
  const customPromptStatusClass =
    customPromptStatus === "saving"
      ? styles.previewBadgeStatusSaving
      : customPromptStatus === "unsaved"
        ? styles.previewBadgeStatusUnsaved
        : customPromptStatus === "saved"
          ? styles.previewBadgeStatusSaved
          : styles.previewBadgeStatusEmpty;
  const previewBadgeLabel =
    activeTab === SMART_CHART_TABS.CUSTOM_PROMPT
      ? customPromptStatusLabel
      : isAdditionalContextExpanded
        ? additionalContextStatusLabel
        : "Preview";
  const previewBadgeClass =
    activeTab === SMART_CHART_TABS.CUSTOM_PROMPT
      ? customPromptStatusClass
      : isAdditionalContextExpanded
        ? previewBadgeStatusClass
        : "";
  const customPromptMacrosByDotPhrase = useMemo(
    () =>
      buildDotPhraseMap(
        sharedMacros,
        mergeTelehealthAvailableMacros(userMacros, hasVvcDotphraseAccess)
      ),
    [sharedMacros, userMacros, hasVvcDotphraseAccess]
  );

  const toggle = useCallback((key, disabled = false) => {
    if (disabled) {
      return;
    }
    setSelections((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const handleFieldActivate = useCallback(
    (key, disabled = false, fieldValue = "") => {
      if (disabled) {
        return;
      }

      if (isAdditionalContextExpanded) {
        setAdditionalContextDraft((previousValue) => {
          const nextValue = toggleFieldTextInAdditionalContext(previousValue, fieldValue);
          const isIncluded = hasFieldTextBlockInAdditionalContext(nextValue, fieldValue);
          setSelections((previousSelections) => ({
            ...previousSelections,
            [key]: isIncluded,
          }));
          return nextValue;
        });
        setAdditionalContextError(null);
        return;
      }

      toggle(key, disabled);
    },
    [isAdditionalContextExpanded, toggle]
  );

  const handleFieldKeyDown = useCallback(
    (event, key, disabled, fieldValue = "") => {
      if (disabled) return;
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        handleFieldActivate(key, disabled, fieldValue);
      }
    },
    [handleFieldActivate]
  );

  const handleSelectAll = useCallback(() => {
    if (isAdditionalContextExpanded) {
      const shouldDeselectAll = areAllSelectableAdditionalContextFieldsIncluded;

      setAdditionalContextDraft((previousValue) => {
        return selectableAdditionalContextFieldEntries.reduce(
          (nextValue, entry) =>
            shouldDeselectAll
              ? removeFieldTextFromAdditionalContext(nextValue, entry.value)
              : appendFieldTextToAdditionalContext(nextValue, entry.value),
          previousValue
        );
      });
      setSelections((previousSelections) => {
        const nextSelections = { ...previousSelections };
        selectableAdditionalContextFieldEntries.forEach((entry) => {
          nextSelections[entry.key] = !shouldDeselectAll;
        });
        return nextSelections;
      });
      setAdditionalContextError(null);
      return;
    }

    setSelections(
      availableFields.reduce((acc, field) => {
        acc[field.key] = Boolean(chartHistoryValues[field.key])
          ? !areAllSelectableFieldsSelected
          : false;
        return acc;
      }, {})
    );
  }, [
    isAdditionalContextExpanded,
    areAllSelectableAdditionalContextFieldsIncluded,
    selectableAdditionalContextFieldEntries,
    availableFields,
    chartHistoryValues,
    areAllSelectableFieldsSelected,
  ]);

  const hydrateSelectionsFromChartHistory = useCallback(
    (sourceChartHistory, options = {}) => {
      const { resetSelections = false } = options;
      const parsed = parseChartHistory(sourceChartHistory);
      const normalizedDeletedFields = normalizeDeletedFields(parsed?.deleted_fields);
      const shouldOpenAdditionalContext = isCustomTemplateChartHistory(parsed);
      const persistedAdditionalContext = normalizeAdditionalContextValue(
        parsed?.custom_template ?? parsed?.additional_context
      );
      const nextAdditionalContext = persistedAdditionalContext
        ? persistedAdditionalContext
        : shouldOpenAdditionalContext
          ? stripSmartChartConsentDisclaimer(savedStartingNote)
          : "";
      const shouldHydrateFromSavedPrechart = isPrechartChartHistory(parsed);
      setChartHistorySource({
        ...parsed,
        deleted_fields: normalizedDeletedFields,
      });
      setAdditionalContextDraft(nextAdditionalContext);
      setSavedAdditionalContext(nextAdditionalContext);
      setPreviewAdditionalContextDraft(shouldOpenAdditionalContext ? nextAdditionalContext : "");
      setIsAdditionalContextExpanded(shouldOpenAdditionalContext);
      setAdditionalContextError(null);

      const fieldsFromHistory = CHAPI_FIELDS.map((field) => {
        const liveValue = getFirstResolvedAliasValue(parsed, field.aliases);
        const deletedValue = normalizeStringValue(
          normalizedDeletedFields[getDeletedFieldStorageKey(field)]
        );
        return { ...field, value: liveValue || deletedValue, liveValue };
      });

      setAvailableFields(fieldsFromHistory);
      const nextChartHistoryValues = fieldsFromHistory.reduce((acc, field) => {
        acc[field.key] = field.value;
        return acc;
      }, {});
      setChartHistoryValues(nextChartHistoryValues);

      if (isAdditionalContextOnlyPatient) {
        setSelections({ ...INITIAL_SELECTIONS });
        return;
      }

      if (resetSelections) {
        setSelections(
          fieldsFromHistory.reduce((acc, field) => {
            acc[field.key] = false;
            return acc;
          }, {})
        );
        return;
      }

      setSelections(
        fieldsFromHistory.reduce((acc, field) => {
          acc[field.key] = shouldHydrateFromSavedPrechart ? Boolean(field.liveValue) : false;
          return acc;
        }, {})
      );
    },
    [isAdditionalContextOnlyPatient, savedStartingNote]
  );

  const fetchChartHistory = useCallback(
    async ({ resetSelections = false } = {}) => {
      if (!patientID) return;

      setIsLoading(true);
      setHasFetchedChartHistory(false);
      setLoadError(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/chart-history`,
          {
            method: "GET",
            headers: { token: getSessionToken() },
          }
        );

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          const message = errorBody?.message || "Unable to load existing smart chart selections.";
          throw new Error(message);
        }

        const data = await response.json();
        if (currentPatientIdRef.current !== patientID) {
          return;
        }
        if (data?.appointment_id || data?.mrn) {
          setFetchedPatientIcn(data.appointment_id || data.mrn);
        }
        const chartHistory = extractChartHistoryPayload(data);
        hydrateSelectionsFromChartHistory(chartHistory, { resetSelections });
      } catch (error) {
        if (currentPatientIdRef.current !== patientID) {
          return;
        }
        console.error("Failed to load chart history in smart chart modal:", error);
        setLoadError(error?.message || "Unable to load existing smart chart selections.");
      } finally {
        if (currentPatientIdRef.current === patientID) {
          setHasFetchedChartHistory(true);
          setIsLoading(false);
        }
      }
    },
    [patientID, hydrateSelectionsFromChartHistory]
  );

  const fetchCustomPromptState = useCallback(async () => {
    if (!patientID) return;

    setCustomPromptError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/smart-chart-state`,
        {
          method: "GET",
          headers: { token: getSessionToken() },
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody?.message || "Unable to load saved custom prompt.";
        throw new Error(message);
      }

      const data = await response.json().catch(() => ({}));
      if (currentPatientIdRef.current !== patientID) {
        return;
      }
      const nextCustomPrompt = normalizeAdditionalContextValue(data?.custom_prompt);
      setCustomPromptDraft(nextCustomPrompt);
      setSavedCustomPrompt(nextCustomPrompt);
      setCustomPromptError(null);
    } catch (error) {
      if (currentPatientIdRef.current !== patientID) {
        return;
      }
      console.error("Failed to load smart chart custom prompt:", error);
      setCustomPromptDraft("");
      setSavedCustomPrompt("");
      setCustomPromptError(error?.message || "Unable to load saved custom prompt.");
    }
  }, [patientID]);

  const fetchPersistedPatientNote = useCallback(async () => {
    if (!patientID) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/note-field`,
        {
          method: "GET",
          headers: { token: getSessionToken() },
        }
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json().catch(() => ({}));
      if (currentPatientIdRef.current !== patientID) {
        return;
      }
      setSavedStartingNote(normalizeAdditionalContextValue(data?.note));
    } catch (error) {
      if (currentPatientIdRef.current !== patientID) {
        return;
      }
      console.error("Failed to load existing patient note:", error);
    }
  }, [patientID]);

  useEffect(() => {
    fetchChartHistory();
  }, [fetchChartHistory]);

  useEffect(() => {
    fetchCustomPromptState();
  }, [fetchCustomPromptState]);

  useEffect(() => {
    fetchPersistedPatientNote();
  }, [fetchPersistedPatientNote]);

  useEffect(() => {
    if (!isAdditionalContextExpanded) {
      return;
    }
    if (!isCustomTemplateChartHistory(chartHistorySource)) {
      return;
    }

    const persistedAdditionalContext = stripSmartChartConsentDisclaimer(savedStartingNote);
    setSavedAdditionalContext(persistedAdditionalContext);
    setAdditionalContextDraft((previousValue) =>
      shouldReplaceAdditionalContextDraft(previousValue, persistedAdditionalContext)
        ? persistedAdditionalContext
        : previousValue
    );
    setPreviewAdditionalContextDraft((previousValue) =>
      shouldReplaceAdditionalContextDraft(previousValue, persistedAdditionalContext)
        ? persistedAdditionalContext
        : previousValue
    );
  }, [isAdditionalContextExpanded, chartHistorySource, savedStartingNote]);

  useEffect(() => {
    hasUserSelectedTabRef.current = false;
    setActiveTab(SMART_CHART_TABS.SMART_CHART);
    setIsAdditionalContextExpanded(false);
    setAdditionalContextDraft("");
    setSavedAdditionalContext("");
    setAdditionalContextError(null);
    setPreviewAdditionalContextDraft("");
    setPreviewNote("");
    setPreviewError(null);
    setChartHistorySource({});
    setChartHistoryValues({});
    setAvailableFields([]);
    setSelections({ ...INITIAL_SELECTIONS });
    setHasFetchedChartHistory(false);
    setCustomPromptDraft("");
    setSavedCustomPrompt("");
    setSavedStartingNote("");
    setCustomPromptError(null);
    setAdditionalContextDictationStatus("");
    setIsAdditionalContextDictating(false);
    setIsAdditionalContextDictationStarting(false);
    setIsSmartAssistDictationActive(false);
    hasFetchedCustomPromptMacrosRef.current = false;
  }, [patientID]);

  const handleTabSelect = useCallback((nextTab) => {
    hasUserSelectedTabRef.current = true;
    setActiveTab(nextTab);
  }, []);

  useEffect(() => {
    const shouldLoadMacros =
      activeTab === SMART_CHART_TABS.CUSTOM_PROMPT || isAdditionalContextExpanded;
    if (!shouldLoadMacros) {
      return;
    }

    if (hasFetchedCustomPromptMacrosRef.current) {
      return;
    }

    if (typeof fetchUserMacros === "function") {
      hasFetchedCustomPromptMacrosRef.current = true;
      fetchUserMacros();
    }
  }, [activeTab, isAdditionalContextExpanded, fetchUserMacros]);

  const readPersistedAdditionalContextFromNote = useCallback(async () => {
    if (!patientID) {
      return stripSmartChartConsentDisclaimer(savedStartingNote);
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/note-field`,
        {
          method: "GET",
          headers: { token: getSessionToken() },
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody?.message || "Unable to load saved Additional EHR Context note.";
        throw new Error(message);
      }

      const data = await response.json().catch(() => ({}));
      const nextStartingNote = normalizeAdditionalContextValue(data?.note);
      setSavedStartingNote(nextStartingNote);
      return stripSmartChartConsentDisclaimer(nextStartingNote);
    } catch (error) {
      console.error("Failed to read persisted smart chart note:", error);
      return stripSmartChartConsentDisclaimer(savedStartingNote);
    }
  }, [patientID, savedStartingNote]);

  const seedAdditionalContextFromPersistedNote = useCallback(async () => {
    const persistedAdditionalContext = await readPersistedAdditionalContextFromNote();
    const selectedFieldValues = availableFields
      .filter((field) => Boolean(selections[field.key]))
      .map((field) =>
        normalizeAdditionalContextValue(
          resolveFieldPreviewValue(field, chartHistorySource, chartHistoryValues)
        ).trim()
      )
      .filter(Boolean);
    const nextAdditionalContext = selectedFieldValues.reduce(
      (nextValue, fieldValue) => appendFieldTextToAdditionalContext(nextValue, fieldValue),
      persistedAdditionalContext
    );

    return { persistedAdditionalContext, nextAdditionalContext };
  }, [
    availableFields,
    selections,
    chartHistorySource,
    chartHistoryValues,
    readPersistedAdditionalContextFromNote,
  ]);

  const handleToggleAdditionalContext = useCallback(async () => {
    if (isAdditionalContextExpanded) {
      setSelections((previousSelections) => {
        const nextSelections = { ...previousSelections };
        selectableAdditionalContextFieldEntries.forEach((entry) => {
          nextSelections[entry.key] = hasFieldTextBlockInAdditionalContext(
            additionalContextDraft,
            entry.value
          );
        });
        return nextSelections;
      });
      setPreviewAdditionalContextDraft("");
      setIsAdditionalContextExpanded(false);
      return;
    }

    const { persistedAdditionalContext, nextAdditionalContext } =
      await seedAdditionalContextFromPersistedNote();

    setAdditionalContextDraft(nextAdditionalContext);
    setPreviewAdditionalContextDraft(nextAdditionalContext);
    setSavedAdditionalContext(persistedAdditionalContext);
    setAdditionalContextError(null);
    setIsAdditionalContextExpanded(true);
  }, [
    isAdditionalContextExpanded,
    additionalContextDraft,
    selectableAdditionalContextFieldEntries,
    seedAdditionalContextFromPersistedNote,
  ]);

  const handleAdditionalContextRowKeyDown = useCallback(
    (event) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        handleToggleAdditionalContext();
      }
    },
    [handleToggleAdditionalContext]
  );

  const upsertAdditionalContextDictationText = useCallback(
    (rawTranscript = "", isFinal = false) => {
      const textarea = customTemplateTextareaRef.current;
      let currentValue = normalizeAdditionalContextValue(textarea?.value ?? additionalContextDraft);
      let selectionStart = textarea?.selectionStart ?? currentValue.length;
      let selectionEnd = textarea?.selectionEnd ?? selectionStart;
      const previousInterimRange = additionalContextDictationInterimRangeRef.current;

      if (previousInterimRange) {
        const previousStart = previousInterimRange.start;
        const previousEnd = previousInterimRange.end;
        currentValue = `${currentValue.slice(0, previousStart)}${currentValue.slice(previousEnd)}`;
        selectionStart = previousStart;
        selectionEnd = previousStart;
      }

      let beforeSelection = currentValue.slice(0, selectionStart);
      const afterSelection = currentValue.slice(selectionEnd);

      let transcript = normalizeSpokenDictation(normalizeAdditionalContextValue(rawTranscript), {
        mode: "plain",
        textBefore: beforeSelection,
      });
      if (!transcript) {
        return;
      }

      const now = Date.now();
      const isPunctuationOnlyTranscript = isDictationPunctuationOnly(transcript);
      const isLineBreakOnlyTranscript = isDictationLineBreakOnly(transcript);
      const shouldInsertWithoutWordSpacing =
        isPunctuationOnlyTranscript || isLineBreakOnlyTranscript;
      if (
        isFinal &&
        shouldInsertWithoutWordSpacing &&
        additionalContextLastFinalDictationRef.current.text === transcript &&
        now - additionalContextLastFinalDictationRef.current.insertedAt < 1200
      ) {
        additionalContextDictationInterimRangeRef.current = null;
        return;
      }

      if (isFinal) {
        const joined = joinDictatedDateAcrossChunks(beforeSelection, transcript, {
          includeMonthNameDates: true,
        });
        beforeSelection = joined.textBefore;
        transcript = joined.transcript;
      }
      const phoneContinuation = normalizePhoneNumberContinuation(beforeSelection, transcript);
      transcript = phoneContinuation.transcript;
      const nextTranscript =
        !shouldInsertWithoutWordSpacing && shouldCapitalizeNextDictationWord(beforeSelection)
          ? capitalizeFirstDictationWord(transcript)
          : transcript;
      const prefix =
        !shouldInsertWithoutWordSpacing &&
        !phoneContinuation.joinsPrevious &&
        beforeSelection.length > 0 &&
        !/\s$/.test(beforeSelection) &&
        !/^[,.;:!?)]/.test(nextTranscript)
          ? " "
          : "";
      const suffix =
        !shouldInsertWithoutWordSpacing &&
        afterSelection.length > 0 &&
        !/^\s|^[,.;:!?)]/.test(afterSelection)
          ? " "
          : "";
      const insertedText = `${prefix}${nextTranscript}${suffix}`;
      const nextValue = `${beforeSelection}${insertedText}${afterSelection}`;
      const insertedStart = beforeSelection.length;
      const insertedEnd = insertedStart + insertedText.length;
      const nextCursor = beforeSelection.length + insertedText.length;

      additionalContextDictationInterimRangeRef.current = isFinal
        ? null
        : {
            start: insertedStart,
            end: insertedEnd,
          };
      if (isFinal) {
        additionalContextLastFinalDictationRef.current = {
          text: nextTranscript,
          insertedAt: now,
        };
      }
      setAdditionalContextDraft(nextValue);
      setAdditionalContextError(null);

      requestAnimationFrame(() => {
        if (!customTemplateTextareaRef.current) {
          return;
        }
        customTemplateTextareaRef.current.focus();
        customTemplateTextareaRef.current.setSelectionRange(nextCursor, nextCursor);
      });
    },
    [additionalContextDraft]
  );

  const stopAdditionalContextDictation = useCallback(
    ({ suppressStoppedToast = false, clearStatus = false } = {}) => {
      const activeRecorder = additionalContextDictationRecorderRef.current;
      if (activeRecorder) {
        additionalContextDictationRecorderRef.current = null;
        if (activeRecorder.state !== "inactive") {
          activeRecorder.stop();
        }
        activeRecorder.ondataavailable = null;
        activeRecorder.onerror = null;
        activeRecorder.onstop = null;
      }

      const activeSocket = additionalContextDictationSocketRef.current;
      if (activeSocket) {
        additionalContextDictationSocketRef.current = null;
        if (typeof WebSocket !== "undefined" && activeSocket.readyState === WebSocket.OPEN) {
          activeSocket.send(JSON.stringify({ type: "CloseStream" }));
        }
        activeSocket.onopen = null;
        activeSocket.onmessage = null;
        activeSocket.onerror = null;
        activeSocket.onclose = null;
        activeSocket.close();
      }

      const activeStream = additionalContextDictationStreamRef.current;
      if (activeStream) {
        additionalContextDictationStreamRef.current = null;
        activeStream.getTracks().forEach((track) => track.stop());
      }

      additionalContextDictationInterimRangeRef.current = null;
      additionalContextLastFinalDictationRef.current = {
        text: "",
        insertedAt: 0,
      };
      setIsAdditionalContextDictationStarting(false);
      setIsAdditionalContextDictating(false);
      setAdditionalContextDictationStatus(clearStatus ? "" : "Dictation stopped.");

      if (!suppressStoppedToast) {
        toast.info("Knowtex dictation stopped.");
      }
    },
    []
  );

  const handleToggleAdditionalContextDictation = useCallback(async () => {
    if (isAdditionalContextDictationStarting) {
      return;
    }

    if (isAdditionalContextDictating) {
      stopAdditionalContextDictation();
      return;
    }

    if (isSmartAssistDictationActive) {
      setAdditionalContextError(
        "Stop Smart Assist dictation before starting Additional EHR Context dictation."
      );
      return;
    }

    if (
      typeof window === "undefined" ||
      typeof window.WebSocket === "undefined" ||
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      toast.error("Live dictation is not supported in this browser.");
      return;
    }

    const recorderMimeType = getSupportedSmartChartRecordingMimeType();
    if (!recorderMimeType) {
      toast.error("This browser does not support microphone dictation for Knowtex.");
      return;
    }

    setIsAdditionalContextDictationStarting(true);
    setAdditionalContextDictationStatus("Starting microphone...");
    setAdditionalContextError(null);

    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch (error) {
        console.error("Failed to access Additional EHR Context dictation microphone:", error);
        throw new Error(getDictationMicrophoneErrorMessage(error));
      }

      additionalContextDictationStreamRef.current = stream;
      setAdditionalContextDictationStatus("Connecting dictation...");

      const tokenResponse = await patientChatApi.createDeepgramToken({
        purpose: "additional-context",
      });

      const queryParams = new URLSearchParams({
        model: tokenResponse.model || "nova-3",
        language: tokenResponse.language || "en-US",
        interim_results: "true",
        punctuate: "false",
        smart_format: "false",
        endpointing: "300",
      });

      const dictationUrl = `${SMART_CHART_DEEPGRAM_WS_URL}?${queryParams.toString()}`;
      const socket = new WebSocket(dictationUrl, ["bearer", tokenResponse.token]);

      additionalContextDictationSocketRef.current = socket;

      socket.onopen = () => {
        const recorder = new MediaRecorder(stream, {
          mimeType: recorderMimeType,
        });

        additionalContextDictationRecorderRef.current = recorder;
        setIsAdditionalContextDictationStarting(false);
        setIsAdditionalContextDictating(true);
        setAdditionalContextDictationStatus("Listening...");

        recorder.ondataavailable = async (event) => {
          if (!event.data || event.data.size === 0 || socket.readyState !== WebSocket.OPEN) {
            return;
          }

          try {
            const chunkBuffer = await event.data.arrayBuffer();
            socket.send(chunkBuffer);
          } catch (error) {
            console.error("Failed to stream Additional EHR Context dictation audio:", error);
          }
        };

        recorder.onerror = (event) => {
          console.error("Additional EHR Context dictation recorder error:", event);
          toast.error("Microphone recording failed.");
          stopAdditionalContextDictation({
            suppressStoppedToast: true,
            clearStatus: true,
          });
        };

        recorder.start(250);
        customTemplateTextareaRef.current?.focus();
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);

          if (payload.type !== "Results") {
            return;
          }

          const transcript = payload.channel?.alternatives?.[0]?.transcript?.trim() || "";
          if (!transcript) {
            return;
          }

          upsertAdditionalContextDictationText(transcript, Boolean(payload.is_final));
        } catch (error) {
          console.error("Failed to parse Additional EHR Context dictation message:", error);
        }
      };

      socket.onerror = (event) => {
        console.error("Additional EHR Context dictation websocket error:", event);
      };

      socket.onclose = (event) => {
        setIsAdditionalContextDictationStarting(false);
        if (event.code !== 1000) {
          toast.error("Knowtex dictation connection failed. Please try again.");
        }

        if (
          additionalContextDictationRecorderRef.current ||
          additionalContextDictationStreamRef.current
        ) {
          stopAdditionalContextDictation({
            suppressStoppedToast: true,
            clearStatus: event.code !== 1000,
          });
        }
      };
    } catch (error) {
      console.error("Failed to start Additional EHR Context dictation:", error);
      stopAdditionalContextDictation({
        suppressStoppedToast: true,
        clearStatus: true,
      });
      const errorMessage =
        error?.message || "Unable to start microphone dictation. Check your browser settings.";
      setAdditionalContextError(errorMessage);
      toast.error(errorMessage);
    }
  }, [
    upsertAdditionalContextDictationText,
    isAdditionalContextDictationStarting,
    isAdditionalContextDictating,
    isSmartAssistDictationActive,
    stopAdditionalContextDictation,
  ]);

  useEffect(() => {
    return () => {
      stopAdditionalContextDictation({
        suppressStoppedToast: true,
        clearStatus: true,
      });
    };
  }, [patientID, stopAdditionalContextDictation]);

  const saveAdditionalContext = useCallback(
    async ({ force = false, draftOverride, restoreFocusTarget = null } = {}) => {
      if (!patientID || isSavingAdditionalContext) {
        return false;
      }

      const nextDraftValue =
        draftOverride === undefined
          ? additionalContextDraft
          : normalizeAdditionalContextValue(draftOverride);
      const comparableDraft = getComparableAdditionalContextValue(nextDraftValue);
      const comparableSaved = getComparableAdditionalContextValue(savedAdditionalContext);

      if (!force && comparableDraft === comparableSaved) {
        setAdditionalContextError(null);
        return false;
      }

      setIsSavingAdditionalContext(true);
      setAdditionalContextError(null);
      setLoadError(null);

      try {
        const normalizedAdditionalContext = comparableDraft;
        const isCustomTemplateActive =
          getComparableAdditionalContextValue(normalizedAdditionalContext).length > 0;
        const noteForAdditionalContext = isCustomTemplateActive
          ? ensureSmartChartConsentDisclaimer(normalizedAdditionalContext)
          : "";
        const nextChartHistoryPatch = normalizedAdditionalContext
          ? {
              custom_template: normalizedAdditionalContext,
              additional_context: "",
              is_custom_template: true,
              is_prechart: false,
            }
          : {
              custom_template: "",
              additional_context: "",
              is_custom_template: false,
              is_prechart: false,
            };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/notes/todos/${patientID}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({
            chartHistory: nextChartHistoryPatch,
            note: noteForAdditionalContext,
          }),
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          const message = errorBody?.message || "Failed to save Additional EHR Context.";
          throw new Error(message);
        }

        setSavedAdditionalContext(normalizedAdditionalContext);
        setAdditionalContextDraft(normalizedAdditionalContext);
        setPreviewAdditionalContextDraft(normalizedAdditionalContext);
        setSavedStartingNote(noteForAdditionalContext);
        setChartHistorySource((previous) => ({
          ...(previous || {}),
          custom_template: isCustomTemplateActive ? normalizedAdditionalContext : "",
          additional_context: "",
          is_custom_template: isCustomTemplateActive,
          is_prechart: false,
        }));

        return true;
      } catch (error) {
        console.error("Failed to save Additional EHR Context:", error);
        setAdditionalContextError(error?.message || "Failed to save Additional EHR Context.");
        return false;
      } finally {
        setIsSavingAdditionalContext(false);
        if (restoreFocusTarget?.isConnected) {
          requestAnimationFrame(() => {
            if (restoreFocusTarget.isConnected && !restoreFocusTarget.disabled) {
              restoreFocusTarget.focus();
            }
          });
        }
      }
    },
    [patientID, isSavingAdditionalContext, additionalContextDraft, savedAdditionalContext]
  );

  const handleClearAdditionalContext = useCallback(async () => {
    setAdditionalContextDraft("");
    setAdditionalContextError(null);
    await saveAdditionalContext({ force: true, draftOverride: "" });
    requestAnimationFrame(() => {
      if (!customTemplateTextareaRef.current) {
        return;
      }
      customTemplateTextareaRef.current.focus();
      customTemplateTextareaRef.current.setSelectionRange(0, 0);
    });
  }, [saveAdditionalContext]);

  const handleOpenPreviousNoteModal = useCallback(() => {
    if (!hasGetLastNoteAccess || !effectivePatientIcn) return;
    setIsPreviousNoteModalOpen(true);
  }, [hasGetLastNoteAccess, effectivePatientIcn]);

  const handleInsertPreviousNote = useCallback(
    (notes) => {
      const sections = (Array.isArray(notes) ? notes : [])
        .filter((note) => note?.text)
        .map((note) => {
          const dateLabel = note.metadata?.date
            ? new Date(note.metadata.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "";
          const separatorHeader = dateLabel
            ? `--- Previous Clinical Note (${dateLabel}) ---`
            : "--- Previous Clinical Note ---";
          return `${separatorHeader}\n\n${note.text.trim()}`;
        });

      if (!sections.length) return;

      const formattedBlock = sections.join("\n\n");
      setAdditionalContextDraft((previous) => {
        const trimmed = (previous || "").trim();
        return trimmed ? `${trimmed}\n\n${formattedBlock}` : formattedBlock;
      });

      if (additionalContextError) {
        setAdditionalContextError(null);
      }

      toast.success(
        sections.length === 1
          ? "Previous note inserted into Additional EHR Context"
          : `${sections.length} notes inserted into Additional EHR Context`
      );

      setTimeout(() => {
        if (customTemplateTextareaRef.current) {
          customTemplateTextareaRef.current.focus();
          const len = customTemplateTextareaRef.current.value.length;
          customTemplateTextareaRef.current.setSelectionRange(len, len);
        }
      }, 50);
    },
    [additionalContextError]
  );

  const saveCustomPrompt = useCallback(
    async ({ force = false, restoreFocusTarget = null } = {}) => {
      if (!patientID || isSavingCustomPrompt) {
        return false;
      }

      const normalizedDraft = normalizeAdditionalContextValue(customPromptDraft);
      const normalizedSaved = normalizeAdditionalContextValue(savedCustomPrompt);

      if (!force && normalizedDraft === normalizedSaved) {
        setCustomPromptError(null);
        return false;
      }

      setIsSavingCustomPrompt(true);
      setCustomPromptError(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/smart-chart-custom-prompt`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              token: getSessionToken(),
            },
            body: JSON.stringify({ customPrompt: normalizedDraft }),
          }
        );

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          const message = errorBody?.message || "Failed to save custom prompt.";
          throw new Error(message);
        }

        setSavedCustomPrompt(normalizedDraft);
        setCustomPromptDraft(normalizedDraft);
        if (typeof onCustomPromptSaved === "function") {
          onCustomPromptSaved({
            patientId: patientID,
            hasCustomPrompt: getComparableAdditionalContextValue(normalizedDraft).length > 0,
          });
        }
        return true;
      } catch (error) {
        console.error("Failed to save custom prompt:", error);
        setCustomPromptError(error?.message || "Failed to save custom prompt.");
        return false;
      } finally {
        setIsSavingCustomPrompt(false);
        if (restoreFocusTarget?.isConnected) {
          requestAnimationFrame(() => {
            if (restoreFocusTarget.isConnected && !restoreFocusTarget.disabled) {
              restoreFocusTarget.focus();
            }
          });
        }
      }
    },
    [patientID, isSavingCustomPrompt, customPromptDraft, savedCustomPrompt, onCustomPromptSaved]
  );

  const handleUseCustomPromptStarter = useCallback(() => {
    // Let the activating click/keypress finish before the empty-state button
    // is removed. This prevents browsers and automation from retrying an
    // activation whose target disappeared during the event sequence.
    requestAnimationFrame(() => {
      setCustomPromptDraft(CUSTOM_PROMPT_STARTER);
      setCustomPromptError(null);

      requestAnimationFrame(() => {
        if (!customPromptTextareaRef.current) return;
        customPromptTextareaRef.current.focus();
      });
    });
  }, []);

  const handleCustomPromptKeyDown = useCallback(
    (event) => {
      if (event.key !== " " && event.key !== "Enter" && event.key !== "Tab") {
        return;
      }

      const textarea = event.currentTarget;
      const selectionStart = textarea.selectionStart ?? 0;
      const selectionEnd = textarea.selectionEnd ?? selectionStart;

      if (selectionStart !== selectionEnd) {
        return;
      }

      const currentValue = textarea.value || "";
      const beforeCursor = currentValue.slice(0, selectionStart);
      const macroMatch = beforeCursor.match(/(?:^|\s)(\.[A-Za-z0-9_-]+)$/);
      const typedDotPhrase = macroMatch?.[1];

      if (!typedDotPhrase) {
        return;
      }

      const macroBody = customPromptMacrosByDotPhrase.get(typedDotPhrase.toLowerCase());

      if (!macroBody) {
        return;
      }

      event.preventDefault();
      const delimiter = event.key === "Enter" ? "\n" : event.key === "Tab" ? "\t" : " ";
      const replaceStart = selectionStart - typedDotPhrase.length;
      const nextValue = `${currentValue.slice(0, replaceStart)}${macroBody}${delimiter}${currentValue.slice(selectionEnd)}`;
      const nextCursor = replaceStart + macroBody.length + delimiter.length;

      setCustomPromptDraft(nextValue);
      setCustomPromptError(null);

      requestAnimationFrame(() => {
        if (!customPromptTextareaRef.current) return;
        customPromptTextareaRef.current.focus();
        customPromptTextareaRef.current.setSelectionRange(nextCursor, nextCursor);
      });
    },
    [customPromptMacrosByDotPhrase]
  );

  const fetchPreviewNote = useCallback(async () => {
    if (!patientID) {
      setPreviewNote("");
      setIsPreviewLoading(false);
      return;
    }
    if (!hasFetchedChartHistory) {
      setIsPreviewLoading(false);
      return;
    }

    const currentCustomTemplate = getComparableAdditionalContextValue(
      previewAdditionalContextDraft
    );
    if (isAdditionalContextExpanded && currentCustomTemplate.length > 0) {
      setPreviewError(null);
      setPreviewNote(ensureSmartChartConsentDisclaimer(currentCustomTemplate));
      setIsPreviewLoading(false);
      return;
    }

    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;
    setIsPreviewLoading(true);
    setPreviewError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/prechart-preview`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({
            selections,
            values: {
              ...chartHistoryValues,
              custom_template: currentCustomTemplate,
            },
            specialty,
          }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody?.message || "Unable to render preview note.";
        throw new Error(message);
      }

      const data = await response.json().catch(() => ({}));
      if (requestId !== previewRequestIdRef.current) {
        return;
      }

      setPreviewNote(typeof data?.preview_note === "string" ? data.preview_note : "");
    } catch (error) {
      if (requestId !== previewRequestIdRef.current) {
        return;
      }
      console.error("Failed to render smart chart preview note:", error);
      setPreviewError(error?.message || "Unable to render preview note.");
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setIsPreviewLoading(false);
      }
    }
  }, [
    patientID,
    selections,
    chartHistoryValues,
    previewAdditionalContextDraft,
    specialty,
    hasFetchedChartHistory,
    isAdditionalContextExpanded,
  ]);

  useEffect(() => {
    fetchPreviewNote();
  }, [fetchPreviewNote]);

  const handleRefresh = useCallback(async () => {
    if (!patientID) return;
    if (isAdditionalContextOnlyPatient) {
      return;
    }
    if (crossSiteCareStatus) {
      setLoadError(crossSiteRelaunchMessage);
      toast.warning(crossSiteRelaunchMessage);
      return;
    }
    setIsAdditionalContextExpanded(false);
    if (isStandaloneUser) {
      setLoadError("Patient enrichment is disabled for standalone users.");
      toast.info("Patient enrichment is disabled for standalone users.");
      return;
    }

    setIsRefreshing(true);
    setLoadError(null);
    setSelections(
      CHAPI_FIELDS.reduce((acc, field) => {
        acc[field.key] = false;
        return acc;
      }, {})
    );

    try {
      const baseUrl = localStorage.getItem("va_fhir_server");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/refresh-chapi`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
          body: JSON.stringify({ base_url: baseUrl }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody?.message || "Unable to refresh CHAPI data.";
        throw new Error(message);
      }

      await fetchChartHistory({ resetSelections: true });
    } catch (error) {
      console.error("Failed to refresh CHAPI data in Smart Chart:", error);
      setLoadError(error?.message || "Unable to refresh CHAPI data.");
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  }, [
    fetchChartHistory,
    patientID,
    isStandaloneUser,
    isAdditionalContextOnlyPatient,
    crossSiteCareStatus,
    crossSiteRelaunchMessage,
  ]);

  const handleSubmit = useCallback(async () => {
    const hasSelectedFields = CHAPI_FIELDS.some((field) => Boolean(selections[field.key]));

    if (!patientID) {
      return;
    }

    if (
      !hasSelectedFields &&
      getComparableAdditionalContextValue(
        isAdditionalContextExpanded ? additionalContextDraft : previewAdditionalContextDraft
      ).length === 0 &&
      !hasExistingPrechartState &&
      !hasCustomPromptDraft
    ) {
      return;
    }

    setIsSubmitting(true);
    setLoadError(null);

    try {
      const normalizedCustomTemplate = getComparableAdditionalContextValue(
        isAdditionalContextExpanded ? additionalContextDraft : previewAdditionalContextDraft
      );
      const normalizedCustomPrompt = normalizeAdditionalContextValue(customPromptDraft);
      const shouldBypassConstructor = normalizedCustomTemplate.length > 0;
      const savedSelectionCapacity =
        shouldBypassConstructor || isAdditionalContextOnlyPatient ? 1 : CHAPI_FIELDS.length;
      const savedSelectionCount = shouldBypassConstructor
        ? 1
        : isAdditionalContextOnlyPatient
          ? 0
          : selectedCount;
      const nextChartHistory = {
        ...chartHistorySource,
      };
      if (isAdditionalContextOnlyPatient) {
        nextChartHistory.custom_template = normalizedCustomTemplate;
        nextChartHistory.additional_context = "";
      } else {
        const deletedFields = normalizeDeletedFields(chartHistorySource?.deleted_fields);

        CHAPI_FIELDS.forEach((field) => {
          const currentFieldValue = normalizeStringValue(chartHistoryValues[field.key]);
          const deletedFieldStorageKey = getDeletedFieldStorageKey(field);
          const writableAliases = getWritableAliases(field);

          if (selections[field.key]) {
            if (currentFieldValue) {
              const [primaryAlias, ...secondaryAliases] = writableAliases;
              if (primaryAlias) {
                nextChartHistory[primaryAlias] = currentFieldValue;
              }
              secondaryAliases.forEach((alias) => {
                nextChartHistory[alias] = "";
              });
            }
            delete deletedFields[deletedFieldStorageKey];
            return;
          }

          if (currentFieldValue) {
            deletedFields[deletedFieldStorageKey] = currentFieldValue;
          }

          writableAliases.forEach((alias) => {
            nextChartHistory[alias] = "";
          });
        });

        // Enforce canonical chart_history key for labs.
        delete nextChartHistory.labs;
        // Enforce canonical key for active problems.
        delete nextChartHistory.problems;

        nextChartHistory.deleted_fields = deletedFields;
        nextChartHistory.custom_template = normalizedCustomTemplate;
        nextChartHistory.additional_context = "";
      }
      delete nextChartHistory.additional_context;
      if (shouldBypassConstructor) {
        nextChartHistory.is_custom_template = true;
        nextChartHistory.is_prechart = false;
      } else if (hasSelectedFields) {
        nextChartHistory.is_custom_template = false;
        nextChartHistory.is_prechart = true;
      } else {
        nextChartHistory.is_custom_template = false;
        nextChartHistory.is_prechart = false;
      }

      let nextPreviewNote = ensureSmartChartConsentDisclaimer(normalizedCustomTemplate);
      if (!shouldBypassConstructor) {
        const previewResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/notes/todos/${patientID}/prechart-preview`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token: getSessionToken(),
            },
            body: JSON.stringify({
              selections,
              values: {
                ...chartHistoryValues,
                custom_template: "",
              },
              specialty,
            }),
          }
        );

        if (!previewResponse.ok) {
          const errorBody = await previewResponse.json().catch(() => ({}));
          const message = errorBody?.message || "Unable to render preview note for submission.";
          throw new Error(message);
        }

        const previewData = await previewResponse.json().catch(() => ({}));
        nextPreviewNote =
          typeof previewData?.preview_note === "string" ? previewData.preview_note : "";
      }

      setPreviewNote(nextPreviewNote);
      setPreviewError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/notes/todos/${patientID}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          token: getSessionToken(),
        },
        body: JSON.stringify({
          chartHistory: nextChartHistory,
          note: nextPreviewNote,
          customPrompt: normalizedCustomPrompt,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const message = errorBody?.message || "Failed to save smart chart selections.";
        throw new Error(message);
      }
      logInfo("Smart Chart submitted", "prechart.submitted", {
        patient_id: patientID,
        mode: "smart_chart",
        selected_field_count: savedSelectionCount,
        is_custom_template: shouldBypassConstructor,
        has_custom_prompt: getComparableAdditionalContextValue(normalizedCustomPrompt).length > 0,
        prechart_length: nextPreviewNote.length,
      });

      setSavedAdditionalContext(normalizedCustomTemplate);
      setAdditionalContextDraft(normalizedCustomTemplate);
      setSavedStartingNote(nextPreviewNote);
      setChartHistorySource((previous) => ({
        ...(previous || {}),
        ...nextChartHistory,
      }));
      setSavedCustomPrompt(normalizedCustomPrompt);
      setCustomPromptDraft(normalizedCustomPrompt);
      setCustomPromptError(null);
      if (typeof onCustomPromptSaved === "function") {
        onCustomPromptSaved({
          patientId: patientID,
          hasCustomPrompt: getComparableAdditionalContextValue(normalizedCustomPrompt).length > 0,
        });
      }
      if (typeof onSubmitSuccess === "function") {
        onSubmitSuccess({
          selections,
          chartHistory: nextChartHistory,
          selectionSummary: {
            patientId: patientID,
            selectedCount: savedSelectionCount,
            capacity: savedSelectionCapacity,
          },
        });
      }
    } catch (error) {
      console.error("Failed to submit smart chart selections:", error);
      setLoadError(error?.message || "Failed to save smart chart selections.");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    patientID,
    selections,
    chartHistorySource,
    chartHistoryValues,
    additionalContextDraft,
    previewAdditionalContextDraft,
    customPromptDraft,
    hasCustomPromptDraft,
    hasExistingPrechartState,
    isAdditionalContextOnlyPatient,
    isAdditionalContextExpanded,
    specialty,
    selectedCount,
    onSubmitSuccess,
    onCustomPromptSaved,
  ]);

  const handleClose = useCallback(() => {
    stopAdditionalContextDictation({
      suppressStoppedToast: true,
      clearStatus: true,
    });

    if (typeof onClose === "function") {
      onClose();
      return;
    }
    if (typeof setShowPrechartingModal === "function") {
      setShowPrechartingModal(false);
    }
  }, [onClose, setShowPrechartingModal, stopAdditionalContextDictation]);

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return undefined;

    let animationFrameId = null;

    const measureWorkspace = () => {
      const measuredWidth = workspace.getBoundingClientRect().width;
      const nextWidth = measuredWidth || window.innerWidth;
      setSmartAssistContainerWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth
      );
    };

    const scheduleWorkspaceMeasurement = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(() => {
        animationFrameId = null;
        measureWorkspace();
      });
    };

    scheduleWorkspaceMeasurement();
    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(scheduleWorkspaceMeasurement);
      observer.observe(workspace);
      return () => {
        observer.disconnect();
        if (animationFrameId !== null) {
          cancelAnimationFrame(animationFrameId);
        }
      };
    }

    window.addEventListener("resize", scheduleWorkspaceMeasurement);
    return () => {
      window.removeEventListener("resize", scheduleWorkspaceMeasurement);
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  useEffect(() => {
    if (
      activeTab === SMART_CHART_TABS.SMART_ASSIST &&
      (!isSmartAssistAvailable || isSmartAssistSplitView)
    ) {
      setActiveTab(SMART_CHART_TABS.SMART_CHART);
    }
  }, [activeTab, isSmartAssistAvailable, isSmartAssistSplitView]);

  // The training guide's Smart Assist slides point inside the Smart Assist
  // panel, which is a tab below the split-view width. The guide asks for it
  // with these events because its interaction lock swallows a synthetic click
  // on the tab.
  const tabBeforeTutorialSmartAssistRef = useRef(null);
  useEffect(() => {
    const showSmartAssist = () => {
      if (isSmartAssistSplitView) {
        setIsSmartAssistExpanded(true);
      } else if (isSmartAssistAvailable) {
        setActiveTab((tab) => {
          if (tab !== SMART_CHART_TABS.SMART_ASSIST) {
            tabBeforeTutorialSmartAssistRef.current = tab;
          }
          return SMART_CHART_TABS.SMART_ASSIST;
        });
      }
    };
    const hideSmartAssist = () => {
      const previousTab = tabBeforeTutorialSmartAssistRef.current;
      tabBeforeTutorialSmartAssistRef.current = null;
      if (previousTab) {
        setActiveTab((tab) => (tab === SMART_CHART_TABS.SMART_ASSIST ? previousTab : tab));
      }
    };
    window.addEventListener("tutorial-show-smart-assist", showSmartAssist);
    window.addEventListener("tutorial-hide-smart-assist", hideSmartAssist);
    return () => {
      window.removeEventListener("tutorial-show-smart-assist", showSmartAssist);
      window.removeEventListener("tutorial-hide-smart-assist", hideSmartAssist);
    };
  }, [isSmartAssistAvailable, isSmartAssistSplitView]);

  const handleSummarizePreviousNotes = useCallback(
    (prompt) => {
      setIsPreviousNoteModalOpen(false);
      setQueuedSmartAssistPrompt(prompt);
      if (isSmartAssistSplitView) {
        setIsSmartAssistExpanded(true);
      } else {
        setActiveTab(SMART_CHART_TABS.SMART_ASSIST);
      }
    },
    [isSmartAssistSplitView]
  );

  const handleQueuedSmartAssistPromptConsumed = useCallback(() => {
    setQueuedSmartAssistPrompt("");
  }, []);

  const handleUseSmartAssistReplyAsNote = useCallback(
    (nextNote) => {
      const normalizedNote = stripSmartChartConsentDisclaimer(nextNote);
      setAdditionalContextDraft(normalizedNote);
      setPreviewAdditionalContextDraft(normalizedNote);
      setPreviewNote(ensureSmartChartConsentDisclaimer(normalizedNote));
      setIsAdditionalContextExpanded(true);
      setAdditionalContextError(null);
      if (!isSmartAssistSplitView) {
        setActiveTab(SMART_CHART_TABS.SMART_CHART);
      }
    },
    [isSmartAssistSplitView]
  );

  const handleInsertSmartAssistAsAdditionalContext = useCallback(
    async (replyText) => {
      const normalizedReply = stripSmartChartConsentDisclaimer(replyText || "").trim();
      if (!normalizedReply) {
        return;
      }

      if (isAdditionalContextExpanded) {
        setAdditionalContextDraft((previousValue) =>
          appendFieldTextToAdditionalContext(previousValue, normalizedReply)
        );
        setPreviewAdditionalContextDraft((previousValue) =>
          appendFieldTextToAdditionalContext(previousValue, normalizedReply)
        );
      } else {
        const { persistedAdditionalContext, nextAdditionalContext: seededContext } =
          await seedAdditionalContextFromPersistedNote();
        const nextAdditionalContext = appendFieldTextToAdditionalContext(
          seededContext,
          normalizedReply
        );
        setSavedAdditionalContext(persistedAdditionalContext);
        setAdditionalContextDraft(nextAdditionalContext);
        setPreviewAdditionalContextDraft(nextAdditionalContext);
        setIsAdditionalContextExpanded(true);
      }

      setAdditionalContextError(null);
      if (!isSmartAssistSplitView) {
        setActiveTab(SMART_CHART_TABS.SMART_CHART);
      }
    },
    [isAdditionalContextExpanded, isSmartAssistSplitView, seedAdditionalContextFromPersistedNote]
  );

  const handleSmartAssistDictationStateChange = useCallback(({ isDictating, isStarting }) => {
    setIsSmartAssistDictationActive(Boolean(isDictating || isStarting));
  }, []);

  useEffect(() => {
    if (isPreviousNoteModalOpen) {
      return undefined;
    }

    const handleWorkspaceEscape = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) {
        return;
      }

      const cachedSmartAssistState =
        smartAssistStateCacheRef.current?.patientId === patientID
          ? smartAssistStateCacheRef.current
          : null;
      const hasPendingSmartAssistDraft =
        normalizeAdditionalContextValue(cachedSmartAssistState?.draftPrompt).trim().length > 0 ||
        (Array.isArray(cachedSmartAssistState?.uploadedFiles) &&
          cachedSmartAssistState.uploadedFiles.length > 0);
      if (
        hasUnsavedAdditionalContext ||
        hasUnsavedCustomPrompt ||
        isSavingAdditionalContext ||
        isSavingCustomPrompt ||
        hasPendingSmartAssistDraft
      ) {
        return;
      }

      const eventTarget = event.target;
      if (
        eventTarget instanceof Element &&
        eventTarget.closest(WORKSPACE_ESCAPE_LOCAL_TARGET_SELECTOR)
      ) {
        return;
      }

      event.preventDefault();
      handleClose();
    };

    document.addEventListener("keydown", handleWorkspaceEscape);
    return () => {
      document.removeEventListener("keydown", handleWorkspaceEscape);
    };
  }, [
    handleClose,
    hasUnsavedAdditionalContext,
    hasUnsavedCustomPrompt,
    isPreviousNoteModalOpen,
    isSavingAdditionalContext,
    isSavingCustomPrompt,
    patientID,
  ]);

  const isSmartAssistSplitOpen =
    isSmartAssistAvailable && isSmartAssistSplitView && isSmartAssistExpanded;

  return (
    <div
      ref={workspaceRef}
      className={`${styles.workspaceHost} ${
        isSmartAssistSplitOpen ? styles.workspaceHostWithSmartAssist : ""
      }`}
      style={{ "--smart-assist-panel-width": `${smartAssistWidthPercent}vw` }}
      data-layout="dashboard-workspace"
    >
      {isSmartAssistAvailable && isSmartAssistSplitView && (
        <AskKnowtexSidecar
          isExpanded={isSmartAssistExpanded}
          onToggle={() => setSmartAssistPreferredExpanded(!isSmartAssistExpanded)}
          sidecarId={`smart-chart-smart-assist-${patientID}`}
          className={styles.smartChartSidecar}
          widthPercent={smartAssistWidthPercent}
          onWidthChange={setSmartAssistWidthPercent}
          isFixedPanel
          contentScale={smartAssistContentScale}
        >
          <SmartAssistPanel
            patientId={patientID}
            capabilities={smartAssistCapabilitiesQuery.data}
            note={previewNote || savedStartingNote}
            diarizedTranscript={diarizedTranscript}
            noteCitations={noteCitations}
            chartHistory={smartAssistChartHistory}
            includeFullChartHistory
            onUseAsNote={handleUseSmartAssistReplyAsNote}
            onInsertAsAdditionalContext={handleInsertSmartAssistAsAdditionalContext}
            stateCacheRef={smartAssistStateCacheRef}
            draftSaver={smartAssistDraftSaver}
            isHidden={!isSmartAssistExpanded}
            onMinimize={() => setSmartAssistPreferredExpanded(false)}
            minimizeControlsId={`smart-chart-smart-assist-${patientID}`}
            dotPhraseMacrosByPhrase={customPromptMacrosByDotPhrase}
            isConflictingDictationActive={
              isAdditionalContextDictating || isAdditionalContextDictationStarting
            }
            conflictingDictationMessage="Stop Additional EHR Context dictation before starting Smart Assist dictation."
            onDictationStateChange={handleSmartAssistDictationStateChange}
            queuedPrompt={queuedSmartAssistPrompt}
            onQueuedPromptConsumed={handleQueuedSmartAssistPromptConsumed}
          />
        </AskKnowtexSidecar>
      )}
      <div
        id="precharting_modal_dialog"
        ref={dialogRef}
        role="region"
        aria-labelledby="smart-chart-modal-title"
        aria-describedby={
          activeTab === SMART_CHART_TABS.SMART_CHART
            ? "smart-chart-modal-intro"
            : activeTab === SMART_CHART_TABS.CUSTOM_PROMPT
              ? "custom-prompt-modal-intro"
              : "smart-assist-modal-intro"
        }
        aria-busy={
          isLoading ||
          isSubmitting ||
          isRefreshing ||
          isSavingAdditionalContext ||
          isSavingCustomPrompt
        }
        className={`${styles.workspaceChart} ${
          isSmartAssistSplitOpen ? styles.workspaceChartWithSmartAssist : ""
        }`}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTopRow}>
            <div>
              <h2 id="smart-chart-modal-title" className={styles.title}>
                Smart Chart for: {patientName}
              </h2>
            </div>
            <button
              id="precharting_modal_close"
              type="button"
              ref={closeButtonRef}
              onClick={handleClose}
              aria-label="Close smart chart modal"
              className={styles.closeButton}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </div>

        <div className={styles.tabNav} role="tablist" aria-label="Smart chart tabs">
          <button
            type="button"
            role="tab"
            id="smart-chart-tab"
            aria-controls="smart-chart-tabpanel"
            aria-selected={activeTab === SMART_CHART_TABS.SMART_CHART}
            onClick={() => handleTabSelect(SMART_CHART_TABS.SMART_CHART)}
            className={`${styles.tabButton} ${styles.tabButtonSmart} ${
              activeTab === SMART_CHART_TABS.SMART_CHART ? styles.tabButtonActive : ""
            }`}
          >
            <span className={styles.tabButtonLabel}>Smart Chart</span>
          </button>
          <button
            type="button"
            role="tab"
            id="custom-prompt-tab"
            aria-controls="custom-prompt-tabpanel"
            aria-selected={activeTab === SMART_CHART_TABS.CUSTOM_PROMPT}
            onClick={() => handleTabSelect(SMART_CHART_TABS.CUSTOM_PROMPT)}
            className={`${styles.tabButton} ${styles.tabButtonPrompt} ${
              activeTab === SMART_CHART_TABS.CUSTOM_PROMPT ? styles.tabButtonActive : ""
            }`}
          >
            <span className={styles.tabButtonLabel}>Custom Prompt</span>
            <span
              className={`${styles.tabButtonMeta} ${
                customPromptStatus === "saved"
                  ? styles.tabButtonMetaSaved
                  : customPromptStatus === "unsaved"
                    ? styles.tabButtonMetaUnsaved
                    : customPromptStatus === "saving"
                      ? styles.tabButtonMetaSaving
                      : styles.tabButtonMetaEmpty
              }`}
            >
              {customPromptStatusLabel}
            </span>
          </button>
          {isSmartAssistAvailable && !isSmartAssistSplitView && (
            <button
              type="button"
              role="tab"
              id="smart-assist-tab"
              aria-controls="smart-assist-tabpanel"
              aria-selected={activeTab === SMART_CHART_TABS.SMART_ASSIST}
              onClick={() => handleTabSelect(SMART_CHART_TABS.SMART_ASSIST)}
              className={`${styles.tabButton} ${styles.tabButtonAssist} ${
                activeTab === SMART_CHART_TABS.SMART_ASSIST ? styles.tabButtonActive : ""
              }`}
            >
              <span className={styles.tabButtonLabel}>Smart Assist</span>
            </button>
          )}
        </div>

        {/* Body */}
        <div className={styles.body}>
          {isSmartAssistAvailable && !isSmartAssistSplitView && (
            <div
              role="tabpanel"
              id="smart-assist-tabpanel"
              aria-labelledby="smart-assist-tab"
              aria-hidden={activeTab !== SMART_CHART_TABS.SMART_ASSIST}
              hidden={activeTab !== SMART_CHART_TABS.SMART_ASSIST}
              className={styles.smartAssistTabPanel}
            >
              <SmartAssistPanel
                patientId={patientID}
                capabilities={smartAssistCapabilitiesQuery.data}
                note={previewNote || savedStartingNote}
                diarizedTranscript={diarizedTranscript}
                noteCitations={noteCitations}
                chartHistory={smartAssistChartHistory}
                includeFullChartHistory
                onUseAsNote={handleUseSmartAssistReplyAsNote}
                onInsertAsAdditionalContext={handleInsertSmartAssistAsAdditionalContext}
                embedded
                isHidden={activeTab !== SMART_CHART_TABS.SMART_ASSIST}
                descriptionId="smart-assist-modal-intro"
                stateCacheRef={smartAssistStateCacheRef}
                draftSaver={smartAssistDraftSaver}
                dotPhraseMacrosByPhrase={customPromptMacrosByDotPhrase}
                isConflictingDictationActive={
                  isAdditionalContextDictating || isAdditionalContextDictationStarting
                }
                conflictingDictationMessage="Stop Additional EHR Context dictation before starting Smart Assist dictation."
                onDictationStateChange={handleSmartAssistDictationStateChange}
                queuedPrompt={queuedSmartAssistPrompt}
                onQueuedPromptConsumed={handleQueuedSmartAssistPromptConsumed}
              />
            </div>
          )}
          {activeTab !== SMART_CHART_TABS.SMART_ASSIST &&
            (activeTab === SMART_CHART_TABS.SMART_CHART ? (
              <div
                role="tabpanel"
                id="smart-chart-tabpanel"
                aria-labelledby="smart-chart-tab"
                className={styles.tabPanel}
              >
                <p id="smart-chart-modal-intro" className={styles.controlIntro}>
                  {isAdditionalContextOnlyPatient ? (
                    "This patient does not have an appointment linked, so EHR field sync is unavailable. You can still save an Additional EHR Context note."
                  ) : isStandaloneUser ? (
                    "Select from the EHR fields previously imported for this patient. Refetch Data is unavailable in standalone mode, but you can also use Additional EHR Context and Custom Prompt."
                  ) : (
                    <>
                      Select the fields found from your EHR to include in your note. Click{" "}
                      <strong className={styles.controlIntroStrong}>Refetch Data</strong> to reset
                      your fields - note this will reset your selections and pull in any updated
                      data found in your EHR.
                    </>
                  )}
                </p>

                <div className={styles.topControlRow}>
                  {!isAdditionalContextOnlyPatient && (
                    <div className={styles.controlActions}>
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        disabled={
                          isLoading ||
                          isSubmitting ||
                          (isAdditionalContextExpanded
                            ? !hasSelectableAdditionalContextFields
                            : !hasSelectableFields)
                        }
                        aria-label={
                          isAdditionalContextExpanded
                            ? areAllSelectableAdditionalContextFieldsIncluded
                              ? "Deselect all chart history context"
                              : "Select all available chart history context"
                            : areAllSelectableFieldsSelected
                              ? "Deselect all chart history fields"
                              : "Select all available chart history fields"
                        }
                        className={styles.refreshButton}
                      >
                        {isAdditionalContextExpanded
                          ? areAllSelectableAdditionalContextFieldsIncluded
                            ? "Deselect all"
                            : "Select all"
                          : areAllSelectableFieldsSelected
                            ? "Deselect all"
                            : "Select all"}
                      </button>

                      {!isStandaloneUser && (
                        <button
                          type="button"
                          id="prechart_refetch_button"
                          onClick={handleRefresh}
                          disabled={isRefreshing || Boolean(crossSiteCareStatus)}
                          aria-label={
                            crossSiteCareStatus
                              ? crossSiteRelaunchMessage
                              : isRefreshing
                                ? "Refetching chart data from EHR"
                                : "Refetch chart data from EHR"
                          }
                          title={crossSiteCareStatus ? crossSiteRelaunchMessage : undefined}
                          className={styles.refreshButton}
                        >
                          <RefreshIcon spinning={isRefreshing} />
                          {isRefreshing ? "Refetching..." : "Refetch Data"}
                        </button>
                      )}
                    </div>
                  )}

                  <div className={styles.previewBadgeWrap}>
                    <span
                      className={`${styles.previewBadge} ${styles.previewStatusBadge} ${
                        previewBadgeClass
                      }`}
                      aria-live="polite"
                    >
                      <EyeIcon />
                      {previewBadgeLabel}
                    </span>
                  </div>
                </div>

                {loadError && (
                  <div role="alert" aria-live="assertive" className={styles.errorBanner}>
                    {loadError}
                  </div>
                )}
                {!isAdditionalContextOnlyPatient &&
                  !isLoading &&
                  availableFields.every((field) => !chartHistoryValues[field.key]) &&
                  !loadError && (
                    <div role="status" aria-live="polite" className={styles.emptyBanner}>
                      No chart history fields are available yet. Click Refetch Data to check again.
                    </div>
                  )}

                <div className={styles.bodyGrid}>
                  {/* Left: Controls */}
                  <div className={styles.controlColumn}>
                    <div
                      role="group"
                      data-tutorial="prechart_field_list"
                      aria-label={
                        isAdditionalContextOnlyPatient
                          ? "Additional EHR Context controls"
                          : "Chart history fields to include"
                      }
                      className={styles.fieldList}
                    >
                      {(isAdditionalContextOnlyPatient ? [] : availableFields).map((f) => {
                        const copyPreviewValue = resolveFieldPreviewValue(
                          f,
                          chartHistorySource,
                          chartHistoryValues
                        );
                        const hasFieldValue = copyPreviewValue.length > 0;
                        const isDisabled = !hasFieldValue;
                        const isChecked = isAdditionalContextExpanded
                          ? hasFieldTextBlockInAdditionalContext(
                              additionalContextDraft,
                              copyPreviewValue
                            )
                          : Boolean(selections[f.key]);
                        return (
                          <div
                            key={f.key}
                            id={`prechart_field_${f.key}`}
                            role="checkbox"
                            tabIndex={isDisabled ? -1 : 0}
                            aria-checked={isChecked}
                            aria-disabled={isDisabled}
                            aria-describedby={`prechart_field_${f.key}_guidance`}
                            aria-label={`${f.label}${
                              isDisabled ? (!hasFieldValue ? " (no data from EHR)" : "") : ""
                            }`}
                            onClick={() => handleFieldActivate(f.key, isDisabled, copyPreviewValue)}
                            onKeyDown={(event) =>
                              handleFieldKeyDown(event, f.key, isDisabled, copyPreviewValue)
                            }
                            title={
                              isDisabled
                                ? !hasFieldValue
                                  ? "No data was pulled from your EHR"
                                  : undefined
                                : undefined
                            }
                            className={`${styles.fieldRow} ${
                              isChecked ? styles.fieldRowSelected : ""
                            } ${isDisabled ? styles.fieldRowDisabled : ""}`}
                          >
                            <span
                              aria-hidden="true"
                              data-disabled-tooltip={
                                isDisabled
                                  ? !hasFieldValue
                                    ? "No data was found from your EHR"
                                    : ""
                                  : ""
                              }
                              className={`${styles.checkbox} ${
                                isChecked ? styles.checkboxSelected : ""
                              } ${isDisabled ? styles.checkboxDisabled : ""}`}
                            >
                              {isChecked && <CheckIcon />}
                            </span>
                            <FieldIcon d={f.d} color={f.color} bg={f.bg} />
                            <span className={styles.fieldLabelText}>{f.label}</span>
                            <span
                              className={styles.customTemplateInfoIcon}
                              aria-hidden="true"
                              data-custom-template-tooltip={f.tooltip}
                              onClick={(event) => event.stopPropagation()}
                            >
                              i
                            </span>
                            <span id={`prechart_field_${f.key}_guidance`} className={styles.srOnly}>
                              {f.tooltip}
                            </span>
                          </div>
                        );
                      })}

                      <div
                        id="prechart_additional_context"
                        role="button"
                        tabIndex={0}
                        aria-expanded={isAdditionalContextExpanded}
                        aria-controls="custom-template-preview-panel"
                        aria-describedby="prechart_additional_context_guidance"
                        onClick={handleToggleAdditionalContext}
                        onKeyDown={handleAdditionalContextRowKeyDown}
                        className={`${styles.fieldRow} ${
                          isAdditionalContextExpanded ? styles.additionalContextRowSelected : ""
                        } ${
                          isAdditionalContextExpanded ? styles.additionalContextRowExpanded : ""
                        } ${styles.customTemplateRow}`}
                      >
                        <span
                          aria-hidden="true"
                          className={`${styles.checkbox} ${
                            isAdditionalContextExpanded ? styles.checkboxSelected : ""
                          } ${styles.additionalContextCheckIcon}`}
                        >
                          {isAdditionalContextExpanded && <CheckIcon />}
                        </span>
                        <span className={styles.additionalContextRowLabel}>
                          {CUSTOM_TEMPLATE_FIELD.label}
                        </span>
                        <span id="prechart_additional_context_guidance" className={styles.srOnly}>
                          {CUSTOM_TEMPLATE_CONSTRUCTOR_TOOLTIP}
                        </span>
                        {hasSavedCustomTemplateInChartHistory ? (
                          <span
                            className={`${styles.additionalContextBadge} ${
                              isSavingAdditionalContext
                                ? styles.additionalContextBadgeSaving
                                : hasUnsavedAdditionalContext
                                  ? styles.additionalContextBadgeUnsaved
                                  : styles.additionalContextBadgeSaved
                            } ${styles.additionalContextRowBadge}`}
                            aria-live="polite"
                          >
                            {isSavingAdditionalContext
                              ? "Saving..."
                              : hasUnsavedAdditionalContext
                                ? "Unsaved"
                                : "Saved"}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {!isAdditionalContextOnlyPatient && (
                      <p className={styles.selectedCount} aria-live="polite">
                        {isAdditionalContextExpanded ? (
                          <>
                            <strong className={styles.selectedCountStrong}>
                              {additionalContextInsertedCount}/{totalChapiFieldCount}
                            </strong>{" "}
                            CHAPI inserted into Additional EHR Context
                          </>
                        ) : (
                          <>
                            <strong className={styles.selectedCountStrong}>
                              {selectedCount}/{totalChapiFieldCount}
                            </strong>{" "}
                            CHAPI selected
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Right: Preview */}
                  <div className={styles.previewColumn}>
                    {isAdditionalContextExpanded ? (
                      <div
                        id="custom-template-preview-panel"
                        className={`${styles.additionalContextPreviewPanel} ${styles.previewSwapPane}`}
                      >
                        <div className={styles.additionalContextHeader}>
                          <p className={styles.additionalContextTitle}>Additional EHR Context</p>
                          <div className={styles.additionalContextActions}>
                            <Button
                              type="button"
                              variant="secondary"
                              size="small"
                              onClick={handleToggleAdditionalContextDictation}
                              disabled={isAdditionalContextDictationStarting}
                              icon={isAdditionalContextDictating ? Square : Microphone}
                              className={`${styles.additionalContextDictateButton} ${
                                isAdditionalContextDictating
                                  ? styles.additionalContextDictateButtonActive
                                  : ""
                              }`}
                              aria-label={
                                isAdditionalContextDictating
                                  ? "Stop Additional EHR Context dictation"
                                  : "Start Additional EHR Context dictation"
                              }
                            >
                              {isAdditionalContextDictationStarting
                                ? "Starting..."
                                : isAdditionalContextDictating
                                  ? "Stop"
                                  : "Dictate"}
                            </Button>
                            {additionalContextDictationStatus ? (
                              <span
                                className={styles.additionalContextDictationStatus}
                                role="status"
                                aria-live="polite"
                              >
                                {additionalContextDictationStatus}
                              </span>
                            ) : null}
                            <Button
                              type="button"
                              variant="secondary"
                              size="small"
                              onClick={handleClearAdditionalContext}
                              disabled={
                                isSavingAdditionalContext ||
                                isAdditionalContextDictating ||
                                isAdditionalContextDictationStarting ||
                                getComparableAdditionalContextValue(additionalContextDraft)
                                  .length === 0
                              }
                              className={styles.additionalContextClearButton}
                            >
                              Clear
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              size="small"
                              ref={additionalContextSaveButtonRef}
                              onClick={(event) =>
                                saveAdditionalContext({
                                  force: true,
                                  restoreFocusTarget: event.currentTarget,
                                })
                              }
                              disabled={
                                isSavingAdditionalContext ||
                                isAdditionalContextDictating ||
                                isAdditionalContextDictationStarting
                              }
                              loading={isSavingAdditionalContext}
                              aria-label={isSavingAdditionalContext ? "Saving..." : "Save"}
                              className={styles.additionalContextSaveButton}
                            >
                              {isSavingAdditionalContext ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>

                        <div className={styles.additionalContextTextareaWrapper}>
                          <textarea
                            id="custom_template_textarea"
                            ref={customTemplateTextareaRef}
                            value={additionalContextDraft}
                            onChange={(event) => {
                              setAdditionalContextDraft(event.target.value);
                              if (additionalContextError) {
                                setAdditionalContextError(null);
                              }
                            }}
                            onBlur={(event) =>
                              saveAdditionalContext({
                                force: false,
                                restoreFocusTarget:
                                  event.relatedTarget === additionalContextSaveButtonRef.current
                                    ? additionalContextSaveButtonRef.current
                                    : null,
                              })
                            }
                            placeholder={CUSTOM_TEMPLATE_CONSTRUCTOR_TOOLTIP}
                            className={`${styles.additionalContextPreviewTextarea} ${
                              styles.additionalContextTextarea
                            } ${
                              hasGetLastNoteAccess
                                ? styles.additionalContextPreviewTextareaWithImportButton
                                : ""
                            }`}
                          />
                          {hasGetLastNoteAccess && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="small"
                              onClick={handleOpenPreviousNoteModal}
                              disabled={
                                isSavingAdditionalContext ||
                                isAdditionalContextDictating ||
                                isAdditionalContextDictationStarting ||
                                !effectivePatientIcn
                              }
                              icon={Page}
                              className={styles.importPreviousNoteButton}
                              aria-label="Import clinical notes from VA"
                              title={
                                !effectivePatientIcn
                                  ? "Patient ICN is required to import notes"
                                  : "Import signed clinical notes from VA"
                              }
                            >
                              Import Notes
                            </Button>
                          )}
                        </div>

                        {additionalContextError ? (
                          <p className={styles.additionalContextError} role="alert">
                            {additionalContextError}
                          </p>
                        ) : (
                          <p className={styles.additionalContextHint}>
                            If you use Additional EHR Context without a Custom Prompt, Knowtex will
                            synthesize it into the standard APSO template and carry it forward into
                            the note unless it is explicitly updated during the patient visit.
                          </p>
                        )}
                      </div>
                    ) : (
                      <NotePreview
                        note={previewNote}
                        error={previewError}
                        isLoading={isPreviewLoading}
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div
                role="tabpanel"
                id="custom-prompt-tabpanel"
                aria-labelledby="custom-prompt-tab"
                className={styles.tabPanel}
              >
                <p
                  id="custom-prompt-modal-intro"
                  className={`${styles.controlIntro} ${styles.customPromptIntro}`}
                >
                  <span>
                    Capture reusable custom instructions for this patient. This text is saved
                    separately and is not appended to the submitted note preview.
                  </span>
                  <span
                    className={`${styles.previewBadge} ${styles.previewStatusBadge} ${styles.customPromptIntroBadge} ${customPromptStatusClass}`}
                    aria-live="polite"
                  >
                    {customPromptStatusLabel}
                  </span>
                </p>

                {customPromptError && (
                  <div role="alert" aria-live="assertive" className={styles.errorBanner}>
                    {customPromptError}
                  </div>
                )}

                <div className={styles.additionalContextPreviewPanel}>
                  <div className={styles.additionalContextHeader}>
                    <p className={styles.additionalContextTitle}>Custom Prompt</p>
                    <div className={styles.additionalContextActions}>
                      <button
                        type="button"
                        ref={customPromptSaveButtonRef}
                        onClick={(event) =>
                          saveCustomPrompt({
                            force: true,
                            restoreFocusTarget: event.currentTarget,
                          })
                        }
                        disabled={isSavingCustomPrompt}
                        className={styles.additionalContextSaveButton}
                      >
                        {isSavingCustomPrompt ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>

                  {!hasCustomPromptDraft && (
                    <div className={styles.customPromptTemplateCard}>
                      <div>
                        <p className={styles.customPromptTemplateTitle}>Not sure where to start?</p>
                        <p className={styles.customPromptTemplateText}>
                          Here's an optional outline to create note rules, section order, and
                          section-specific instructions. You can edit every part of it after it is
                          inserted.
                        </p>
                      </div>
                      <button
                        type="button"
                        id="custom_prompt_outline_button"
                        onClick={handleUseCustomPromptStarter}
                        className={styles.customPromptTemplateButton}
                      >
                        Optional Outline
                      </button>
                    </div>
                  )}

                  <textarea
                    id="custom_prompt_textarea"
                    ref={customPromptTextareaRef}
                    value={customPromptDraft}
                    onChange={(event) => {
                      setCustomPromptDraft(event.target.value);
                      if (customPromptError) {
                        setCustomPromptError(null);
                      }
                    }}
                    onKeyDown={handleCustomPromptKeyDown}
                    onBlur={(event) =>
                      saveCustomPrompt({
                        force: false,
                        restoreFocusTarget:
                          event.relatedTarget === customPromptSaveButtonRef.current
                            ? customPromptSaveButtonRef.current
                            : null,
                      })
                    }
                    placeholder="Add custom instructions for this patient. Use macros to quickly insert your saved dot phrases."
                    className={`${styles.additionalContextPreviewTextarea} ${styles.customPromptTextarea}`}
                  />

                  <p className={styles.additionalContextHint}>
                    Type any saved dot phrase (for example, <code>.myphrase</code>) and press space,
                    enter, or tab to expand it inline. Progress saves when you click out or press
                    Save.
                  </p>
                  <p className={styles.customPromptMacroFooter}>
                    Have a prompt you like? Save it as a dot phrase so you can reuse it quickly in
                    future patient visits.
                  </p>
                </div>
              </div>
            ))}
        </div>

        {/* Footer */}
        <div className={`${styles.footer} ${styles.workspaceFooter}`}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className={styles.footerButton}
          >
            {activeTab === SMART_CHART_TABS.SMART_CHART ? "Skip" : "Close"}
          </Button>
          {activeTab !== SMART_CHART_TABS.SMART_ASSIST && (
            <Button
              type="button"
              variant="primary"
              onClickAsync={handleSubmit}
              disabled={isSaveDisabled}
              successLabel="Saved!"
              aria-label={isSubmitting ? "Submitting smart chart selections" : undefined}
              className={styles.footerButton}
            >
              Save Smart Chart
            </Button>
          )}
        </div>
      </div>

      {hasGetLastNoteAccess && (
        <PreviousNoteModal
          isOpen={isPreviousNoteModalOpen}
          onClose={() => setIsPreviousNoteModalOpen(false)}
          onInsertNote={handleInsertPreviousNote}
          patientName={patientName}
          doctorName={
            typeof localStorage !== "undefined" ? localStorage.getItem("userName") || "" : ""
          }
          patientIcn={effectivePatientIcn}
          patientId={patientID}
          isSmartAssistAvailable={isSmartAssistAvailable}
          onSummarizeWithSmartAssist={
            isSmartAssistAvailable && patientID ? handleSummarizePreviousNotes : undefined
          }
          sta3n={
            crossSiteCareStatus?.sta3n ||
            (typeof localStorage !== "undefined"
              ? localStorage.getItem("va_sta3n") || "500"
              : "500")
          }
        />
      )}
    </div>
  );
}
